import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { toggleSound, setLanguage, saveSettings } from '../store/slices/uiSlice';
import { db, clearLocalData } from '../utils/db';
import { generateUUID } from '../utils/security';
import { badgeService } from '../services/badgeService';
import { useTranslation } from '../hooks/useTranslation';
import '../styles/SettingsPage.css';

interface SettingsPageProps {
  onClose: () => void;
}

const SettingsPage: React.FC<SettingsPageProps> = ({ onClose }) => {
  const dispatch = useDispatch();
  const { soundEnabled, language } = useSelector((state: RootState) => state.ui);
  const [exportStatus, setExportStatus] = useState<string>('');
  const [importStatus, setImportStatus] = useState<string>('');
  const [storageInfo, setStorageInfo] = useState<{ habitsCount: number; recordsCount: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  
  const updateStorageInfo = async () => {
    try {
      const habitsCount = await db.habits.count();
      const recordsCount = await db.records.count();
      setStorageInfo({ habitsCount, recordsCount });
    } catch (error) {
      console.error('Storage info error:', error);
    }
  };

  useEffect(() => {
    updateStorageInfo();
    
    const emailElement = document.querySelector('.email-contact');
    if (emailElement) {
      const user = emailElement.querySelector('[data-user]')?.getAttribute('data-user') || '';
      const domain = emailElement.querySelector('[data-domain]')?.getAttribute('data-domain') || '';
      if (user && domain) {
        const email = user + '@' + domain;
        const link = document.createElement('a');
        link.href = `mailto:${email}`;
        link.textContent = email;
        link.style.cssText = 'color: inherit; text-decoration: none;';
        emailElement.replaceChildren(link);
      }
    }
  }, []);

  const handleSoundToggle = () => {
    const nextValue = !soundEnabled;
    dispatch(toggleSound());
    dispatch(saveSettings({ soundEnabled: nextValue }) as any);
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextLang = e.target.value as 'ja' | 'en';
    dispatch(setLanguage(nextLang));
    dispatch(saveSettings({ language: nextLang }) as any);
  };

  const exportData = async () => {
    try {
      setExportStatus(t('settings.exporting'));
      
      const habits = await db.habits.toArray();
      const records = await db.records.toArray();
      const stats = badgeService.getStats();
      
      const exportData = {
        version: '4.0 (Pure IndexedDB)',
        exportedAt: new Date().toISOString(),
        appName: 'hibi',
        habits: habits,
        records: records,
        stats: stats
      };
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      });
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `hibi-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setExportStatus(t('settings.exportComplete'));
      setTimeout(() => setExportStatus(''), 3000);
    } catch (error) {
      console.error('Export error:', error);
      setExportStatus(t('settings.exportFailed'));
      setTimeout(() => setExportStatus(''), 3000);
    }
  };

  const importData = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // ファイルサイズチェック (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setImportStatus(t('settings.importFailed'));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    try {
      setImportStatus(t('settings.importing'));
      const text = await file.text();

      let data: any;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error('Invalid JSON format');
      }

      if (!Array.isArray(data.habits) || !Array.isArray(data.records)) {
        throw new Error('Invalid format: habits and records must be arrays');
      }

      if (!window.confirm(t('settings.importWarning'))) {
        setImportStatus('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      await clearLocalData();

      // 旧形式(localStorageService)の id(UUID文字列) → uuid マッピング
      const habitUuidMap = new Map<string, string>();

      // habits を正規化（旧形式・新形式両対応）
      const normalizedHabits = data.habits.map((h: any, index: number) => {
        // 旧形式は id が UUID文字列、新形式は uuid フィールドを持つ
        const uuid = (typeof h.uuid === 'string' && h.uuid)
          ? h.uuid
          : (typeof h.id === 'string' && h.id)
            ? h.id
            : generateUUID();

        // 旧形式の id(UUID) → uuid のマッピングを保存
        if (typeof h.id === 'string') {
          habitUuidMap.set(h.id, uuid);
        }

        return {
          // id は省略してauto-incrementに任せる
          uuid,
          name: typeof h.name === 'string' ? h.name : '不明',
          description: h.description || '',
          color: h.color || '#4F46E5',
          icon: h.icon || '',
          repeatDays: h.repeatDays ?? {
            mon: true, tue: true, wed: true, thu: true,
            fri: true, sat: true, sun: true,
          },
          // 旧形式: frequency='weekly' → goalType='weekly'
          goalType: h.goalType ?? (h.frequency === 'weekly' ? 'weekly' : 'continuous'),
          weeklyGoal: h.weeklyGoal,
          // 旧形式: orderIndex、新形式: displayOrder
          displayOrder: typeof h.displayOrder === 'number' ? h.displayOrder
            : typeof h.orderIndex === 'number' ? h.orderIndex
            : index,
          // boolean/number 両対応
          isArchived: (h.isArchived === true || h.isArchived === 1) ? 1 : 0,
          currentStreak: h.currentStreak ?? 0,
          bestStreak: h.bestStreak ?? 0,
          totalCompleted: h.totalCompleted ?? 0,
          reminderEnabled: (h.reminderEnabled === true || h.reminderEnabled === 1) ? 1 : 0,
          reminderTime: h.reminderTime ?? '09:00',
          createdAt: h.createdAt ? new Date(h.createdAt) : new Date(),
          updatedAt: h.updatedAt ? new Date(h.updatedAt) : new Date(),
        };
      });

      // records を正規化（旧形式・新形式両対応）
      const normalizedRecords = data.records.map((r: any) => {
        // 旧形式: habitId(UUID文字列)、新形式: habitUuid
        const habitUuid = r.habitUuid
          || habitUuidMap.get(r.habitId)
          || r.habitId
          || '';

        // 旧形式: completed(boolean)、新形式: status
        const status = r.status
          || (r.completed ? 'completed' : 'pending');

        return {
          // id は省略してauto-incrementに任せる
          uuid: (typeof r.uuid === 'string' && r.uuid)
            ? r.uuid
            : (typeof r.id === 'string' && r.id)
              ? r.id
              : generateUUID(),
          habitUuid,
          date: r.date,
          status,
          note: r.note ?? r.notes,
          completedAt: r.completedAt ? new Date(r.completedAt) : undefined,
          createdAt: r.createdAt ? new Date(r.createdAt) : new Date(),
          updatedAt: r.updatedAt ? new Date(r.updatedAt) : new Date(),
        };
      });

      await db.habits.bulkPut(normalizedHabits);
      await db.records.bulkPut(normalizedRecords);

      if (data.stats && typeof data.stats === 'object') {
        await db.stats.put({
          ...data.stats,
          id: 'current',
          joinedDate: data.stats.joinedDate ? new Date(data.stats.joinedDate) : new Date(),
        });
      }

      setImportStatus(t('settings.importComplete'));
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error('Import error:', error);
      setImportStatus(t('settings.importFailed'));
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clearAllData = async () => {
    if (window.confirm(t('settings.deleteWarning'))) {
      try {
        await clearLocalData();
        
        // 移行フラグ等は消さず、アプリの設定に関わる項目のみ削除
        localStorage.removeItem('syukan_settings');
        localStorage.removeItem('hibi_badges');
        localStorage.removeItem('hibi_user_stats');
        
        alert(t('settings.deleteSuccess'));
        window.location.reload();
      } catch (error) {
        console.error('Clear data error:', error);
        alert(t('settings.deleteFailed'));
      }
    }
  };

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-content" onClick={(e) => e.stopPropagation()}>
        <div className="settings-header">
          <h2>⚙️ {t('settings.title')}</h2>
          <button className="settings-close" onClick={onClose}>×</button>
        </div>
        
        <div className="settings-body">
          <section className="settings-section">
            <h3>🌐 {t('settings.language')}</h3>
            <div className="settings-item">
              <div className="settings-item-info">
                <h4>{t('settings.language')}</h4>
              </div>
              <select 
                className="settings-select"
                value={language || 'ja'}
                onChange={handleLanguageChange}
              >
                <option value="ja">日本語</option>
                <option value="en">English</option>
              </select>
            </div>
          </section>

          <section className="settings-section">
            <h3>🔊 {t('settings.soundSettings')}</h3>
            <div className="settings-item">
              <div className="settings-item-info">
                <h4>{t('settings.soundEffects')}</h4>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={soundEnabled}
                  onChange={handleSoundToggle}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </section>

          <section className="settings-section">
            <h3>📊 {t('settings.dataManagement')}</h3>
            <div className="settings-item">
              <button className="settings-button primary" onClick={exportData}>
                📥 {t('settings.exportData')}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={importData}
                style={{ display: 'none' }}
                id="import-file-input"
              />
              <label htmlFor="import-file-input" className="settings-button primary" style={{ cursor: 'pointer', margin: 0 }}>
                📤 {t('settings.importData')}
              </label>
            </div>
            
            {exportStatus && <div className="export-status success">{exportStatus}</div>}
            {importStatus && <div className="export-status success">{importStatus}</div>}

            <div className="settings-item">
              <div className="settings-item-info">
                <p>
                  {storageInfo ? (
                    <>{t('settings.habitsCount')}: {storageInfo.habitsCount}, {t('settings.recordsCount')}: {storageInfo.recordsCount}</>
                  ) : (
                    t('common.loading')
                  )}
                </p>
              </div>
            </div>

            <div className="settings-item danger">
              <button className="settings-button danger" onClick={clearAllData}>
                🗑️ {t('settings.deleteAllData')}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
